import { useMemo, useState } from 'react';
import type { Nullable } from 'corgi-types';

import { articleToTrackingProps } from 'common/tracking/articleToTrackingProps';
import type { Accessory } from 'common/types/accessory-slider';
import type { ProductCampaign } from 'common/types/product-card';
import { useAfterSlideHandler } from './handlers';
import { useViewsTracker } from './tracking';

export { useAccessoryCart } from './use-accessory-cart';

interface UseProductImpressionsOptions {
  accessories: Array<Nullable<Accessory>>;
  slidesToShow?: number;
}

const isAccessory = (accessory: Nullable<Accessory>): accessory is Accessory => accessory !== null;

const isProductCampaign = (campaign: Nullable<ProductCampaign>): campaign is ProductCampaign =>
  campaign !== null;

const normalizeAccessory = (accessory: Accessory): Accessory => ({
  ...accessory,
  campaigns: accessory.campaigns?.filter(isProductCampaign) ?? [],
});

const getSellableWithTracking = (items: Array<Nullable<Accessory>>) =>
  items
    .filter(isAccessory)
    .map(normalizeAccessory)
    .filter(({ sellable }) => sellable)
    .map(item => ({ ...item, tracking: articleToTrackingProps(item) }));

export const useAccessories = ({ accessories, slidesToShow }: UseProductImpressionsOptions) => {
  const [viewedItems, setViewedItems] = useState([0]);
  const sellableItems = useMemo(() => getSellableWithTracking(accessories), [accessories]);
  const handleAfterSlide = useAfterSlideHandler({
    viewedItems,
    setViewedItems,
  });

  useViewsTracker({
    viewedItems,
    sellableItems,
    slidesToShow,
  });

  return { sellableItems, handleAfterSlide };
};
